import {MinusOutlined, PlusOutlined} from '@ant-design/icons';
import {Button, Form, Input, Modal, Select} from 'antd';
import PropTypes from 'prop-types';
import {useEffect} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {
  RQCategoryRegistrationAPI,
  RQUpdateCategoryAPI,
} from '../../../../../src/reactQuery/apis/rqGetProductCategory';
import {selectAuth} from '../../../../../src/redux/selectors/authSelector';

const {Option} = Select;

const CategoryModal = ({open, onCancel, refetch, updateCategoryData}) => {
  const [form] = Form.useForm();
  const token = useSelector(selectAuth)?.tokenData;
  
  const createCategory = RQCategoryRegistrationAPI({
    onSuccess: () => {
      toast.success('Category created successfully');
      refetch();
      form.resetFields();
      onCancel();
    },
    onError: error => {
      const errorMessage =
    error?.response?.data?.message ||   
    error?.data?.message ||            
    error?.message ||                  
    'Failed to add Brand';

      toast.error(errorMessage);
    },
  });

  const updateCategory = RQUpdateCategoryAPI({
    onSuccess: () => {
      toast.success('Category updated successfully');
      refetch();
      form.resetFields();
      onCancel();
    },
    onError: error => {
      const errorMessage =
    error?.response?.data?.message ||  
    error?.data?.message ||             
    error?.message ||                   
    'Failed to add Brand';

      toast.error(errorMessage);
    },
  });

  useEffect(() => {
    if (!open) {return;}

    if (updateCategoryData) {
      form.setFieldsValue({
        name: updateCategoryData.name,
        isActive: updateCategoryData.isActive,
        tax: {
          hsnCode: updateCategoryData.tax?.hsnCode,
          cgst: updateCategoryData.tax?.cgst,
          sgst: updateCategoryData.tax?.sgst,
          igst: updateCategoryData.tax?.igst,
        },
        features: updateCategoryData.features?.features || [],
      });
    } else {
      form.resetFields();
    }
  }, [open, updateCategoryData, form]);

  const onFinish = values => {
    const payload = {
      name: values.name,
      hsnCode: values.tax.hsnCode,
      cgst: Number(values.tax.cgst),
      sgst: Number(values.tax.sgst),
      igst: Number(values.tax.igst || 0),
      isActive: values.isActive,
      features: {
        features: values.features || [],
      },
    };

    if (updateCategoryData) {
      updateCategory.mutate({
        token,
        id: updateCategoryData.id,
        formData: payload,
      });
    } else {
      createCategory.mutate({
        token,
        formData: payload,
      });
    }
  };

  return (
    <Modal
      open={open}
      title={updateCategoryData ? 'Update Category' : 'Add Category'}
      footer={null}
      width={700}
      centered
      destroyOnClose
      onCancel={() => {
        form.resetFields();
        onCancel();
      }}
    >
      <Form form={form} layout={'vertical'} onFinish={onFinish}>
        <Form.Item
          label={'Category Name'}
          name={'name'}
          rules={[{required: true, message: 'Category name is required'}]}
          getValueFromEvent={e => e.target.value.toUpperCase()} 
        >
          <Input placeholder={'Enter category name'} />
        </Form.Item>
       
        <div className={'grid grid-cols-4 gap-3'}>
          <Form.Item
            label={'HSN Code'}
            name={['tax', 'hsnCode']}
            rules={[{required: true}]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label={'CGST %'}
            name={['tax', 'cgst']}
            rules={[
              {required: true, message: 'Please enter CGST %'},
              {
                type: 'number',
                min: 0,
                max: 100,
                message: 'CGST must be between 0 and 100',
                transform: value => Number(value),
              },
            ]}
          >
            <Input type={'number'} />
          </Form.Item>

          <Form.Item
            label={'SGST %'}
            name={['tax', 'sgst']}
            rules={[
              {required: true, message: 'Please enter SGST %'},
              {
                type: 'number',
                min: 0,
                max: 100,
                message: 'SGST must be between 0 and 100',
                transform: value => Number(value),
              },
            ]}
          >
            <Input type={'number'} />
          </Form.Item>

          <Form.Item
            label={'IGST %'}
            name={['tax', 'igst']}
            rules={[
              {required: true, message: 'Please enter IGST %'},
              {
                type: 'number',
                min: 0,
                max: 100,
                message: 'IGST must be between 0 and 100',
                transform: value => Number(value),
              },
            ]}
          >
            <Input type={'number'} />
          </Form.Item>
        </div>
        
        <Form.List name={'features'}>
          {(fields, {add, remove}) => (
            <>
              <p className={'font-medium mt-2'}>Features</p>
              {fields.map(({key, name}) => (
                <div key={key} className={'flex gap-2 mb-2'}>
                  <Form.Item
                    name={name}
                    rules={[{required: true, message: 'Feature is required'}]}
                    className={'flex-1'}
                  >
                    <Input placeholder={'Enter feature'} />
                  </Form.Item>
                  <Button danger onClick={() => remove(name)}>
                    <MinusOutlined />
                  </Button>
                </div>
              ))}
              <Button block type={'dashed'} onClick={() => add()}>
                <PlusOutlined /> Add Feature
              </Button>
            </>
          )}
        </Form.List>
        
        <Form.Item
          label={'Status'}
          name={'isActive'}
          rules={[{required: true, message: 'Select status'}]}
        >
          <Select placeholder={'Select status'}>
            <Option value={true}>Active</Option>
            <Option value={false}>Inactive</Option>
          </Select>
        </Form.Item>
        
        <div className={'flex justify-end gap-2'}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button
            type={'primary'}
            htmlType={'submit'}
            loading={createCategory.isLoading || updateCategory.isLoading}
          >
            {updateCategoryData ? 'Update' : 'Save'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

CategoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  refetch: PropTypes.func.isRequired,
  updateCategoryData: PropTypes.object,
};

export default CategoryModal;
